/* Legal pages — Iter 44 · Ley 1581 de 2012 · Habita compliance.
 * All three pages share the same layout skeleton to keep them consistent
 * and easy to maintain. Copy is intentionally in plain Spanish, with
 * concrete legal references where relevant.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Lock, Cookie as CookieIcon } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const POLICY_VERSION = '2026-02-01';

function LegalShell({ title, icon: Icon, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-white" data-testid={testid}>
      <Header />
      <div className="bg-habita-cream border-b border-habita/10">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10 lg:py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-habita mb-4">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-11 h-11 rounded-2xl bg-habita text-white flex items-center justify-center"><Icon className="w-5 h-5" /></span>
            <p className="text-xs uppercase tracking-[0.25em] text-habita font-semibold">Legal · Habita</p>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark leading-tight">{title}</h1>
          {subtitle && <p className="text-gray-600 mt-3 max-w-2xl leading-relaxed">{subtitle}</p>}
          <p className="text-xs text-gray-500 mt-4">Versión vigente: <strong>{POLICY_VERSION}</strong></p>
        </div>
      </div>
      <main className="max-w-3xl mx-auto px-4 lg:px-8 py-10 lg:py-14 prose prose-slate prose-headings:font-serif prose-headings:text-habita-dark prose-a:text-habita">
        {children}
      </main>
      <Footer />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Política de Tratamiento de Datos Personales (Ley 1581 de 2012)

export function DataPolicy() {
  return (
    <LegalShell
      testid="legal-data-policy"
      icon={Shield}
      title="Política de Tratamiento de Datos Personales"
      subtitle="Habita cumple con la Ley 1581 de 2012 y el Decreto 1377 de 2013 de la República de Colombia."
    >
      <SEO title="Política de Tratamiento de Datos · habita.co" description="Cómo Habita recolecta, usa y protege tus datos personales conforme a la Ley 1581 de 2012." />

      <h2>1. Responsable del tratamiento</h2>
      <p><strong>habita.co</strong> — plataforma inmobiliaria digital con domicilio en Colombia. Correo del oficial de privacidad: <a href="mailto:privacidad@gethabita.co">privacidad@gethabita.co</a>.</p>

      <h2>2. Datos que recolectamos</h2>
      <ul>
        <li><strong>Datos de identificación:</strong> nombre, correo, teléfono, ciudad, número de documento (cuando lo suministres).</li>
        <li><strong>Datos de la publicación:</strong> propiedades, precios, fotografías, videos, ubicación aproximada.</li>
        <li><strong>Datos técnicos:</strong> dirección IP, tipo de dispositivo, navegador, páginas visitadas.</li>
        <li><strong>Datos de terceros:</strong> únicamente cuando conectas un CRM externo (Wasi, Tokko, Inmovilla) mediante credenciales cifradas.</li>
      </ul>

      <h2>3. Finalidades del tratamiento</h2>
      <ol>
        <li>Prestar el servicio de intermediación inmobiliaria (búsqueda, publicación, contacto).</li>
        <li>Verificar la autenticidad de las publicaciones y prevenir fraude.</li>
        <li>Enviar notificaciones operativas (nuevas visitas, mensajes, cambios de estado).</li>
        <li>Enviar comunicaciones comerciales <strong>únicamente si otorgas consentimiento explícito</strong>.</li>
        <li>Cumplir con obligaciones legales y contables.</li>
      </ol>

      <h2>4. Tus derechos como titular</h2>
      <p>Como titular de los datos tienes derecho a:</p>
      <ul>
        <li>Conocer, actualizar y rectificar tus datos personales.</li>
        <li>Solicitar prueba de la autorización otorgada.</li>
        <li>Ser informado sobre el uso que se ha dado a tus datos.</li>
        <li>Presentar quejas ante la Superintendencia de Industria y Comercio.</li>
        <li>Revocar la autorización y solicitar la supresión del dato cuando no exista un deber legal para conservarlo.</li>
        <li>Acceder gratuitamente a tus datos personales que hayan sido objeto de tratamiento.</li>
      </ul>
      <p>Puedes ejercer estos derechos desde el <Link to="/mi-cuenta/privacidad">Centro de Privacidad</Link> en tu cuenta o mediante el <Link to="/habeas-data">formulario de Habeas Data</Link>.</p>

      <h2>5. Vigencia y conservación</h2>
      <p>Conservamos los datos por el tiempo estrictamente necesario para las finalidades autorizadas o mientras exista una relación contractual vigente. Cuando solicitas la eliminación de tu cuenta, procedemos con la supresión salvo obligación legal de conservación.</p>

      <h2>6. Transferencias internacionales</h2>
      <p>Los datos pueden ser almacenados en servidores en la nube ubicados fuera de Colombia (proveedores con estándares SOC 2 o ISO 27001). Estos proveedores actúan como encargados del tratamiento bajo las mismas obligaciones que Habita.</p>

      <h2>7. Medidas de seguridad</h2>
      <p>Aplicamos cifrado en tránsito (HTTPS + HSTS), cifrado en reposo para credenciales sensibles (Fernet/AES-128), control de acceso basado en roles, auditoría de accesos y masking automático en logs. Ninguna medida es infalible; te recomendamos usar contraseñas fuertes y no compartir tus credenciales.</p>

      <h2>8. Contacto para ejercer tus derechos</h2>
      <p>Escríbenos a <a href="mailto:privacidad@gethabita.co">privacidad@gethabita.co</a> o utiliza el formulario público de <Link to="/habeas-data">Habeas Data</Link>. Responderemos dentro de los 15 días hábiles establecidos por la ley.</p>

      <h2>9. Vigencia de esta política</h2>
      <p>Esta política entra en vigor el <strong>{POLICY_VERSION}</strong>. Cualquier modificación material será notificada por correo y publicada en esta página.</p>
    </LegalShell>
  );
}

// ────────────────────────────────────────────────────────────────
// Política de Privacidad

export function Privacy() {
  return (
    <LegalShell
      testid="legal-privacy"
      icon={Lock}
      title="Política de Privacidad"
      subtitle="Cómo tratamos tu información cuando usas Habita, ya sea como visitante, propietario, agente o inmobiliaria."
    >
      <SEO title="Política de Privacidad · habita.co" description="Política de privacidad de habita.co: qué datos recogemos, para qué, y tus opciones de control." />

      <h2>1. Alcance</h2>
      <p>Esta política aplica a todos los usuarios de habita.co, incluyendo visitantes anónimos, cuentas registradas, agentes verificados e inmobiliarias.</p>

      <h2>2. Información que recopilamos automáticamente</h2>
      <ul>
        <li>Dirección IP y ubicación aproximada (a nivel de ciudad).</li>
        <li>Páginas visitadas, tiempo de sesión, dispositivo y navegador.</li>
        <li>Interacciones con propiedades (vistas, favoritos, contactos).</li>
      </ul>

      <h2>3. Información que suministras voluntariamente</h2>
      <ul>
        <li>Al registrarte: nombre, correo, teléfono.</li>
        <li>Al publicar: datos del inmueble, fotos, ubicación.</li>
        <li>Al contactar un agente: mensaje, número (si eliges compartirlo).</li>
      </ul>

      <h2>4. Uso de cookies</h2>
      <p>Utilizamos cookies estrictamente necesarias para el funcionamiento del sitio (sesión, seguridad) y cookies analíticas (Google Analytics, Microsoft Clarity, Meta Pixel) <strong>solo con tu consentimiento</strong>. Puedes gestionar tus preferencias en el banner de cookies o desde tu Centro de Privacidad.</p>

      <h2>5. Terceros con los que compartimos información</h2>
      <p>Compartimos datos con:</p>
      <ul>
        <li>Agentes o inmobiliarias solo cuando <strong>tú les envías un mensaje o solicitud de contacto</strong>.</li>
        <li>Proveedores de infraestructura (hospedaje, correo transaccional, analítica) bajo contrato de encargado de tratamiento.</li>
        <li>Autoridades competentes cuando exista un requerimiento legal.</li>
      </ul>
      <p>Nunca vendemos ni cedemos tus datos a terceros con fines comerciales sin tu autorización expresa.</p>

      <h2>6. Cómo proteges tu cuenta</h2>
      <ul>
        <li>Usa contraseñas de al menos 8 caracteres con letras, números y símbolos.</li>
        <li>No compartas tus credenciales de acceso.</li>
        <li>Cierra sesión en dispositivos compartidos.</li>
        <li>Habilita cualquier verificación adicional que ofrezcamos en el futuro.</li>
      </ul>

      <h2>7. Retención y eliminación</h2>
      <p>Conservamos tus datos mientras tengas cuenta activa. Puedes solicitar la eliminación desde <Link to="/mi-cuenta/privacidad">tu Centro de Privacidad</Link>; procesaremos la solicitud en un plazo máximo de 14 días.</p>

      <h2>8. Cambios a esta política</h2>
      <p>Si actualizamos esta política te notificaremos por correo y te pediremos re-consentimiento cuando el cambio sea material.</p>
    </LegalShell>
  );
}

// ────────────────────────────────────────────────────────────────
// Términos y Condiciones

export function Terms() {
  return (
    <LegalShell
      testid="legal-terms"
      icon={FileText}
      title="Términos y Condiciones de Uso"
      subtitle="Al usar habita.co aceptas estos términos. Léelos con atención antes de crear tu cuenta o publicar."
    >
      <SEO title="Términos y Condiciones · habita.co" description="Términos de uso del marketplace inmobiliario habita.co." />

      <h2>1. Objeto</h2>
      <p>Habita es una plataforma tecnológica que facilita la intermediación entre propietarios, agentes, inmobiliarias y personas interesadas en comprar o arrendar inmuebles en Colombia. Habita no es propietario, arrendador ni vendedor de los inmuebles publicados.</p>

      <h2>2. Registro y responsabilidad de la cuenta</h2>
      <ul>
        <li>Debes ser mayor de 18 años para registrarte.</li>
        <li>Eres responsable de la veracidad de la información que suministras.</li>
        <li>Eres responsable de mantener la confidencialidad de tu contraseña.</li>
      </ul>

      <h2>3. Publicación de inmuebles</h2>
      <ul>
        <li>Solo puedes publicar inmuebles sobre los que tengas facultad legal de disposición o representación.</li>
        <li>Las fotografías, precios y descripciones deben corresponder a la realidad del inmueble.</li>
        <li>Habita se reserva el derecho de moderar, editar o retirar publicaciones que infrinjan estos términos o la ley colombiana.</li>
      </ul>

      <h2>4. Uso prohibido</h2>
      <p>Está prohibido usar Habita para:</p>
      <ul>
        <li>Publicar inmuebles falsos o duplicados con precios engañosos.</li>
        <li>Realizar cobros por adelantado sin contrato firmado.</li>
        <li>Enviar comunicaciones no solicitadas (spam) a otros usuarios.</li>
        <li>Extraer masivamente datos del sitio mediante scraping o técnicas automatizadas no autorizadas.</li>
      </ul>

      <h2>5. Propiedad intelectual</h2>
      <p>El diseño, código, marca, textos y elementos gráficos de Habita son propiedad de habita.co. Las fotografías y descripciones de los inmuebles pertenecen a quien las publicó, quien otorga a Habita una licencia no exclusiva para desplegarlas en la plataforma.</p>

      <h2>6. Limitación de responsabilidad</h2>
      <p>Habita actúa como intermediario tecnológico. No garantiza la veracidad final de cada publicación, la disponibilidad de los inmuebles ni el éxito de las transacciones. Recomendamos siempre verificar la propiedad en la ORIP correspondiente y firmar los contratos por escrito.</p>

      <h2>7. Modificaciones</h2>
      <p>Podemos modificar estos términos con notificación previa por correo y publicación en esta página. El uso continuado del servicio implica la aceptación de los nuevos términos.</p>

      <h2>8. Ley aplicable y jurisdicción</h2>
      <p>Estos términos se rigen por las leyes de la República de Colombia. Cualquier controversia será resuelta en los tribunales de Bogotá D.C.</p>

      <h2>9. Contacto</h2>
      <p>Para consultas sobre estos términos: <a href="mailto:contacto@gethabita.co">contacto@gethabita.co</a>.</p>
    </LegalShell>
  );
}

// ────────────────────────────────────────────────────────────────
// Uso de Cookies

export function CookiePolicy() {
  return (
    <LegalShell
      testid="legal-cookies"
      icon={CookieIcon}
      title="Política de Cookies"
      subtitle="Qué cookies usamos, para qué, y cómo gestionar tu consentimiento."
    >
      <SEO title="Política de Cookies · habita.co" description="Uso de cookies en habita.co y cómo gestionar tu consentimiento." />

      <h2>1. ¿Qué son las cookies?</h2>
      <p>Son pequeños archivos que se guardan en tu dispositivo cuando visitas un sitio web. Permiten reconocerte y recordar tus preferencias.</p>

      <h2>2. Tipos de cookies que usamos</h2>
      <ul>
        <li><strong>Estrictamente necesarias.</strong> Sesión, seguridad, prevención de fraude. No requieren consentimiento porque son indispensables para el funcionamiento.</li>
        <li><strong>Analíticas.</strong> Google Analytics 4, Microsoft Clarity. Nos permiten entender cómo se usa el sitio de forma agregada y anónima.</li>
        <li><strong>Publicitarias.</strong> Meta Pixel. Miden el impacto de nuestras campañas.</li>
      </ul>

      <h2>3. ¿Cómo controlas tus cookies?</h2>
      <p>Al ingresar por primera vez mostramos un banner donde puedes <strong>aceptar todas</strong>, <strong>solo las necesarias</strong> o <strong>configurar</strong>. Puedes cambiar tu decisión en cualquier momento desde tu <Link to="/mi-cuenta/privacidad">Centro de Privacidad</Link>.</p>

      <h2>4. Cookies de terceros</h2>
      <p>Los proveedores mencionados tienen sus propias políticas de privacidad y controlan las cookies que instalan directamente. Enlaces:</p>
      <ul>
        <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google — Política de privacidad</a></li>
        <li><a href="https://privacy.microsoft.com/es-ES/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft — Política de privacidad</a></li>
        <li><a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta — Política de privacidad</a></li>
      </ul>
    </LegalShell>
  );
}
